import { cache } from 'react';
import { logger } from '@tuic/logger';
import { UpdateChecklistItem } from '../../generated/account';
import { Locale } from '@tuic/i18n';
import { Account } from '../../index';

export const getChecklist = async (
  locale: Locale,
  idToken: string,
  bookingCode: string,
): Promise<Account.Checklist | null> => {
  let { data, error } = await Account.getChecklistData({
    path: {
      bookingCode,
    },
    query: {
      locale,
    },
    auth: idToken,
  });

  if (error) {
    let args = {
      locale,
      bookingCode,
      idToken,
    };

    logger.error(`Could not fetch checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return null;
  }

  return data ?? null;
};

export const memoizedGetChecklist = cache(getChecklist);

export const addChecklistItem = async (
  args: Account.AddChecklistItem & {
    idToken: string;
    bookingCode: string;
  },
): Promise<boolean> => {
  let { idToken, bookingCode, ...body } = args;

  let { data, error } = await Account.addChecklistItem({
    body,
    path: {
      bookingCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not add item to checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};

export const updateChecklistItem = async (
  args: UpdateChecklistItem & {
    idToken: string;
    bookingCode: string;
    checklistItemCode: string;
  },
): Promise<boolean> => {
  let { idToken, bookingCode, checklistItemCode, ...body } = args;

  let { data, error } = await Account.updateChecklistItem({
    body,
    path: {
      bookingCode,
      code: checklistItemCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not update item in checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};

export const deleteChecklistItem = async (args: {
  idToken: string;
  bookingCode: string;
  checklistItemCode: string;
  type: Account.UpdateChecklistItem['type'];
}): Promise<boolean> => {
  let { idToken, bookingCode, checklistItemCode, type } = args;

  let { data, error } = await Account.updateChecklistItem({
    body: {
      type,
      removed: true,
    },
    path: {
      bookingCode,
      code: checklistItemCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not delete item in checklist. ${JSON.stringify(args)}`);
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};
